import {
  DatabaseAuto,
  IdataArrayResponse,
  IfilterProps,
  ItaxPayout,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Represents a tax payout in the system.
 * This class provides methods to manage and track tax payouts,
 * including retrieval, updating, and filtering of tax payout records.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link TaxPayout} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const taxPayout = await TaxPayout.getAll();
 *
 * const taxPayout = await TaxPayout.filterAll({
 *
 * });
 *
 * const taxPayout = await TaxPayout.bulkDelete({
 *
 * });
 *
 * const taxPayout = await TaxPayout.getOne('resourceId');
 *
 * const taxPayout = await TaxPayout.remove();
 *
 * ```
 */
export class TaxPayout extends DatabaseAuto {
  /** Base API endpoint for tax payout operations */
  static readonly baseUrl = '/tax-payout';

  /** Unique reference ID for the tax payout */
  readonly urId: ItaxPayout['urId'];

  /** Total amount of the tax payout */
  readonly total: ItaxPayout['total'];

  /** Date when the tax payout was initiated */
  readonly dateInitiated: ItaxPayout['dateInitiated'];

  /** Date when the tax payout was completed */
  readonly dateCompleted: ItaxPayout['dateCompleted'];

  /** Current status of the tax payout */
  readonly status: ItaxPayout['status'];

  /** Company associated with the tax payout */
  readonly company: ItaxPayout['company'];

  /** Payment metadata */
  readonly paymentMeta?: ItaxPayout['paymentMeta'];

  /**
   * Creates a new instance of TaxPayout.
   * @param {ItaxPayout} data - The tax payout data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: ItaxPayout) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      }
    });

    super(data);

    this.urId = data.urId;
    this.total = data.total;
    this.dateInitiated = data.dateInitiated;
    this.dateCompleted = data.dateCompleted;
    this.status = data.status;
    this.company = data.company;
    this.paymentMeta = data.paymentMeta;
  }

  /**
   * Retrieves all tax payouts with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: TaxPayout[] }>}
   * An object containing the total count and an array of tax payouts
   * @throws {Error} If there's an error fetching the tax payouts
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<ItaxPayout>>(`${TaxPayout.baseUrl}?${query}`);

    const taxPayouts = await lastValueFrom(observer$);

    return {
      count: taxPayouts.count,
      dataArr: taxPayouts.data.map(val => new TaxPayout(val))
    };
  }

  /**
   * Filters tax payouts based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: TaxPayout[] }>}
   * An object containing the total count and an array of filtered tax payouts
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<ItaxPayout>>(
        `${TaxPayout.baseUrl}/filter`,
        filter
      );

    const taxPayouts = await lastValueFrom(observer$);

    return {
      count: taxPayouts.count,
      dataArr: taxPayouts.data.map(val => new TaxPayout(val))
    };
  }

  /**
   * Retrieves a single tax payout by its ID.
   * @param {string} resourceId - The unique identifier of the tax payout to retrieve
   * @returns {Promise<TaxPayout>} The requested tax payout
   * @throws {Error} If the tax payout is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<ItaxPayout>(`${TaxPayout.baseUrl}/${resourceId}`);
    const taxPayout = await lastValueFrom(observer$);

    return new TaxPayout(taxPayout);
  }

  /**
   * Updates the status of the current tax payout.
   * @param {{ status: ItaxPayout['status'] }} payload - The new status for the tax payout
   * @returns {Promise<TaxPayout>} The updated tax payout
   * @throws {Error} If the update operation fails or the tax payout doesn't exist
   */
  async update(payload: { status: ItaxPayout['status'] }) {
    const observer$ = CommerceClient.ehttp
      .makePut<ItaxPayout>(
        `${TaxPayout.baseUrl}/${this._id}`,
        payload
      );

    return new TaxPayout(await lastValueFrom(observer$));
  }
}
